import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  fetchMonetizationJourneyBoard,
  type ExperimentAssignment,
  type MonetizationJourneyColumn as JourneyColumn,
} from "../experiments/experimentClient";
import { MonetizationContactCard } from "./MonetizationContactCard";

export function MonetizationJourneyColumn({
  experimentId,
  variant,
  column,
  onSelect,
}: {
  experimentId: string;
  variant: string | null;
  column: JourneyColumn;
  onSelect: (assignment: ExperimentAssignment) => void;
}) {
  const [extra, setExtra] = useState<ExperimentAssignment[]>([]);
  const [hasMore, setHasMore] = useState(column.has_more);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const assignments = [...column.assignments, ...extra];

  const loadMore = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMonetizationJourneyBoard({
        experimentId,
        variant,
        column: column.key,
        offset: assignments.length,
        limit: column.limit,
      });
      const next = result.columns[0];
      setExtra((current) => [...current, ...(next?.assignments ?? [])]);
      setHasMore(next?.has_more ?? false);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Could not load more contacts.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex w-72 shrink-0 flex-col rounded-lg border bg-muted/20">
      <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <h3 className="truncate text-sm font-semibold">{column.label}</h3>
        <Badge variant="secondary" className="tabular-nums">
          {column.total}
        </Badge>
      </header>
      <div className="flex min-h-40 flex-1 flex-col gap-2 p-2">
        {assignments.map((assignment) => (
          <MonetizationContactCard
            key={assignment.assignment_id}
            assignment={assignment}
            onSelect={onSelect}
          />
        ))}
        {!assignments.length ? (
          <div className="flex min-h-28 items-center justify-center rounded-md border border-dashed px-4 text-center text-xs text-muted-foreground">
            No contacts here yet.
          </div>
        ) : null}
        {error ? (
          <p className="px-1 text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {hasMore ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadMore()}
            disabled={loading}
          >
            {loading
              ? "Loading…"
              : `Load more (${assignments.length}/${column.total})`}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
